/* REQUEST PROMISE ************************************************************/

// utils
import _ from 'lodash'

/*
  get(url, data, opts) -> Promise
  post(url, data, opts) -> Promise
  put(url, data, opts) -> Promise
  delete(url, data, opts) -> Promise
  request(method, url, data, opts) -> Promise
*/

/* Constants ******************************************************************/

export const HTTP_VERB = {
  GET: "GET",
  POST: "POST",
  PUT: "PUT",
  DELETE: "DELETE",
}

export const CONTENT_TYPE = {
  JSON: 'application/json',
  TEXT: 'text/plain',
  BINARY: 'application/octet-stream'
}

export const RESPONSE_TYPE = {
  ARRAYBUFFER: 'arraybuffer',
  BLOB: 'blob',
  DOCUMENT: 'document',
  JSON: 'json',
  TEXT: 'text',
}

/* Defaults ******************************************************************/

let defaultOpts = {
  async: true,
  responseType: RESPONSE_TYPE.JSON,
  header: {
    'Accept': '*/*',
    'Content-Type': 'application/json',
  },
  timeout: 60 * 60 * 1000, // 1h
}

function ensureOpts(opts) {
  return Object.assign(
    {},
    defaultOpts,
    opts,
    {
      header: Object.assign({}, defaultOpts.header, opts?opts.header:{})
    }
  )
}

/* Middleware *****************************************************************/

let log = false
let requestMiddleware = [
  (request) => { if(log) console.log("request", request) }
]

export function addRequestMiddleware(func) {
  requestMiddleware.push(func)
}

let responseMiddleware = [
  (request) => { if(log) console.log("response", request)},
  (request) => { request.error = request.status != 200 }
]
export function addResponseMiddleware(func) {
    responseMiddleware.push(func)
}

/* Aliases ********************************************************************/

export function get(...args) {
  return requestPromise.apply(null, [HTTP_VERB.GET, ...args])
}

export function post(...args) {
  return requestPromise.apply(null, [HTTP_VERB.POST, ...args])
}

export function put(...args) {
  return requestPromise.apply(null, [HTTP_VERB.PUT, ...args])
}

export function del(...args) {
  return requestPromise.apply(null, [HTTP_VERB.DELETE, ...args])
}

export function formatParams(params) {
  if (!params) return ""
  return "?" + Object
    .keys(params)
    .map(function(key){
      return key+"="+encodeURIComponent(params[key])
    })
    .join("&")
}

export function UberPromise(executor) {
  let t = {}
  let thenCallbacks = []
  let catchCallbacks = []
  let progressCallbacks = []

  let resolveSet = []
  let rejectSet = []
  let progressSet = []

  t.then = (callback) => {
    resolveSet.forEach((r)=>{
      callback.apply(null, r)
    })
    thenCallbacks.push(callback)
    return t
  }

  t.catch = (callback) => {
    rejectSet.forEach((r)=>{
      callback.apply(null, r)
    })
    catchCallbacks.push(callback)
    return t
  }

  t.progress = (callback) => {
    progressSet.forEach((r)=>{
      callback.apply(null, r)
    })
    progressCallbacks.push(callback)
    return t
  }

  const resolve = (...args) => {
    resolveSet.push(args)
    for(let i in thenCallbacks) thenCallbacks[i].apply(null, args)
  }

  const reject = (...args) => {
    rejectSet.push(args)
    for(let i in catchCallbacks) catchCallbacks[i].apply(null, args)
  }

  const progress = (...args) => {
    progressSet.push(args)
    for(let i in progressCallbacks) progressCallbacks[i].apply(null, args)
  }

  executor(resolve, reject, progress)

  return t
}

/* Request Promise Implementation *********************************************/

/**
* Request Promise Factory method
* Manages HTTP Request Stuff acts like a promise but provides a optional
* progress callback
*/
export function requestPromise(method, url, data, opts) {
  let xhr = new XMLHttpRequest()
  let t = UberPromise((resolve, reject, progress) => {
    opts = ensureOpts(opts) // ensure defaults
    let scope = {
      method, // request method (GET, PUT, DELETE, POST)
      url, // request url
      data, // data that should be sent
      header: opts.header, // ie. [{'Accept':'*/*'}]
      timeout: opts.timeout, // timeout in msec
      async: opts.async, // boolean async flag
      responseType: opts.responseType, // ie. arraybuffer, default is text,
      resolveProgress: opts.resolveProgress
    }
    // one shot request transform that acts like Middleware
    if (opts.requestTransform) opts.requestTransform(scope)
    // actual Middleware calls
    for (let i = 0; i < requestMiddleware.length; i++) {
      if (requestMiddleware[i](scope) === false) break
    }

    xhr.open(scope.method, scope.url, scope.async)
    // add headers
    _.each(scope.header, (value, header) => {
      if(value) xhr.setRequestHeader(header, value)
    })
    xhr.responseType = scope.responseType
    xhr.timeout = scope.timeout
    /*xhr.addEventListener("load", (e) => { console.log("load", e) } , false);
    xhr.addEventListener("error", (e) => { console.log("error", e) } , false);*/
    xhr.addEventListener("abort", (e) => { console.log("abort", e) } , false);
    xhr.upload.addEventListener(
      "progress",
      progress,
      false
    )
    xhr.onprogress =  (e) => {
      // console.log("onprogress", e)
    }
    /*xhr.onloadstart =  (e) => { console.log("onloadstart", e) }
    xhr.onloadend =  (e) => { console.log("onloadend", e) }*/
    xhr.onreadystatechange = () => {
      // console.log("onreadystatechange", xhr.readyState)
      // invoked on xhr state change. waiting for readyState 4
      if(xhr.readyState === XMLHttpRequest.DONE) {
        scope.status = xhr.status
        scope.statusText = xhr.statusText
        scope.response = xhr.response
        scope.error = false
        // apply Middleware
        for(let i = 0; i < responseMiddleware.length; i++) {
          if(responseMiddleware[i](scope) === false) break
        }
        if (scope.error) {
          reject({
            message: scope.message,
            status: scope.status,
            response: scope.response
          })
        } else {
          // one shot transform
          if (opts.responseTransform) opts.responseTransform(scope)
          resolve(scope.response)
        }
      }
    }
    // encode and send
    if (
      _.isTypedArray(scope.data) ||
      Object.prototype.toString.call(data) === '[object File]'
    ) {
      xhr.send(scope.data)
    } else if (scope.data || _.isBoolean(scope.data)) {
      xhr.send(JSON.stringify(scope.data))
    } else {
      xhr.send(null)
    }
  })

  t.cancel = () => {
    console.log("xhr cancel")
    xhr.abort()
  }

  return t
}

/* Image Data Util ************************************************************/

export function toDataUrl(buffer) {
  var binary = ''
  var bytes = new Uint8Array(buffer)
  var len = bytes.byteLength
  for (var i = 0; i < len; i++) binary += String.fromCharCode(bytes[i])
  return 'data:image/jpeg;base64,' + btoa(binary)
}



// WEBPACK FOOTER //
// ./src/app/api/utils.js